import { DEFAULT_SHOP_LOCALE, type LocaleResolutionOptions, type ShopLocale } from './shared'
import { normalizeActiveShopLocales, normalizeShopLocale } from './normalization'
import { stripLocalePrefix } from './path'

export const resolveLocaleFromAcceptLanguage = (
  acceptLanguage: string | string[] | undefined,
  options?: LocaleResolutionOptions,
): ShopLocale | null => {
  if (!acceptLanguage) return null
  const header = Array.isArray(acceptLanguage) ? acceptLanguage.join(',') : acceptLanguage
  const tokens = header
    .split(',')
    .map((entry) => entry.split(';')[0]?.trim().toLowerCase())
    .filter(Boolean)

  for (const token of tokens) {
    const locale = normalizeShopLocale(token, options)
    if (locale) return locale

    const languageCandidate = token.split('-')[0]
    const fromLanguage = normalizeShopLocale(languageCandidate, options)
    if (fromLanguage) return fromLanguage
  }

  return null
}

export const resolveShopLocale = ({
  pathname,
  cookieLocale,
  profileLocale,
  acceptLanguage,
  fallbackLocale = DEFAULT_SHOP_LOCALE,
  availableLocales,
}: {
  pathname: string
  cookieLocale?: ShopLocale | null
  profileLocale?: string | null
  acceptLanguage?: string | string[] | undefined
  fallbackLocale?: ShopLocale
  availableLocales?: ShopLocale[]
}): ShopLocale => {
  const strictOptions: LocaleResolutionOptions = {
    availableLocales: normalizeActiveShopLocales(availableLocales),
  }
  const { localeFromPath } = stripLocalePrefix(pathname, strictOptions)
  const cookie = normalizeShopLocale(cookieLocale, strictOptions)
  const profile = normalizeShopLocale(profileLocale, strictOptions)
  const accepted = resolveLocaleFromAcceptLanguage(acceptLanguage, strictOptions)
  const fallback = normalizeShopLocale(fallbackLocale, strictOptions) ?? DEFAULT_SHOP_LOCALE

  return localeFromPath ?? cookie ?? profile ?? accepted ?? fallback
}
